import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSession } from '@clerk/clerk-react';
import { MagnifyingGlass, Buildings, User, Warning, Plus, CircleNotch } from '@phosphor-icons/react';
import * as Sentry from '@sentry/react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { RenoAddEmailInline } from './RenoAddEmailInline';

export interface RenoRecipientSearchResult {
  id: number;
  type: 'contact' | 'company';
  name: string;
  email: string;
  hasEmail: boolean;
  subtitle: string;
}

export interface AddedRecipient {
  /** null bij een vrij ingetypt adres. */
  id: number | null;
  type: 'contact' | 'company' | 'manual';
  name: string;
  email: string;
}

interface Props {
  onAdd: (recipient: AddedRecipient) => void;
  /** Adressen die al in de lijst staan; worden als "toegevoegd" gemarkeerd. */
  existingEmails?: string[];
}

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Zoekt vrij door alle contactpersonen én firma's zodat een renovatieticket ook
 * gemaild kan worden naar iemand die niet aan het ticket, het renovatieproject of
 * het projectonderdeel hangt. Zoeken/filteren gebeurt backend-side; hier wordt enkel
 * getoond wat de API teruggeeft.
 */
export function RenoRecipientSearch({ onAdd, existingEmails = [] }: Props) {
  const { session } = useSession();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<RenoRecipientSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);

  const alreadyAdded = useMemo(
    () => new Set(existingEmails.map((e) => e.trim().toLowerCase()).filter(Boolean)),
    [existingEmails],
  );

  const search = useCallback(
    async (term: string) => {
      if (!session) return;
      const current = ++requestId.current;
      setIsLoading(true);
      setError(null);
      try {
        const res = await fetchWithAuth(
          `${API_URL}/reno-helpdesk/recipients/search?q=${encodeURIComponent(term)}`,
          { method: 'GET' },
          session,
        );
        if (!res.ok) throw new Error(`Zoeken mislukt (${res.status})`);
        const data: RenoRecipientSearchResult[] = await res.json();
        // Antwoord van een oudere toetsaanslag negeren.
        if (current === requestId.current) setResults(data);
      } catch (err) {
        if (current !== requestId.current) return;
        Sentry.captureException(err, {
          tags: { component: 'RenoRecipientSearch' },
          extra: { term },
        });
        setError('Zoeken mislukt. Probeer opnieuw.');
        setResults([]);
      } finally {
        if (current === requestId.current) setIsLoading(false);
      }
    },
    [session],
  );

  useEffect(() => {
    const term = query.trim();
    if (term.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setIsLoading(false);
      return;
    }
    const timer = setTimeout(() => search(term), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, search]);

  const typedEmail = query.trim();
  const canAddTypedEmail =
    EMAIL_PATTERN.test(typedEmail) &&
    !results.some((r) => r.email.toLowerCase() === typedEmail.toLowerCase()) &&
    !alreadyAdded.has(typedEmail.toLowerCase());

  const handleAdd = (recipient: AddedRecipient) => {
    onAdd(recipient);
    setQuery('');
    setResults([]);
  };

  return (
    <div className="border-b border-neutral-primary/10 px-2 pb-2 pt-1">
      <div className="flex items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1.5">
        <MagnifyingGlass size={13} className="shrink-0 text-neutral-secondary-mid" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Andere contactpersoon of firma..."
          className="w-full bg-transparent text-xs text-neutral-primary outline-none placeholder:text-neutral-secondary-mid"
        />
        {isLoading && <CircleNotch size={12} className="shrink-0 animate-spin text-neutral-secondary-mid" />}
      </div>

      {error && <p className="px-1 pt-1.5 text-[10px] text-status-bad">{error}</p>}

      {!error && query.trim().length >= MIN_QUERY_LENGTH && !isLoading && results.length === 0 && !canAddTypedEmail && (
        <p className="px-1 pt-1.5 text-[10px] text-neutral-secondary-mid">
          Niets gevonden. Typ een volledig e-mailadres om het rechtstreeks toe te voegen.
        </p>
      )}

      {(results.length > 0 || canAddTypedEmail) && (
        <div className="mt-1.5 max-h-48 overflow-y-auto rounded-md border border-muted-foreground/20 bg-background">
          {canAddTypedEmail && (
            <button
              type="button"
              onClick={() => handleAdd({ id: null, type: 'manual', name: typedEmail, email: typedEmail })}
              className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs text-neutral-primary transition-colors hover:bg-neutral-primary/5"
            >
              <Plus size={13} className="shrink-0 text-moss-primary" />
              <span className="min-w-0 flex-1 truncate">
                Adres gebruiken: <span className="font-medium">{typedEmail}</span>
              </span>
            </button>
          )}

          {results.map((result) => {
            const isAdded = alreadyAdded.has(result.email.toLowerCase());
            const disabled = !result.hasEmail || isAdded;

            // Zonder adres: geen "dood" resultaat tonen maar het adres laten aanvullen.
            if (!result.hasEmail) {
              return (
                <div
                  key={`${result.type}-${result.id}`}
                  className="flex items-start gap-2 bg-amber-50/60 px-2 py-1.5"
                >
                  <Warning size={13} weight="fill" className="mt-0.5 shrink-0 text-amber-700" />
                  <div className="min-w-0 flex-1 space-y-1">
                    <span className="block truncate text-xs text-amber-900">{result.name}</span>
                    <span className="block truncate text-[10px] text-amber-800">
                      {result.subtitle} — geen e-mailadres
                    </span>
                    <RenoAddEmailInline
                      compact
                      type={result.type}
                      id={result.id}
                      name={result.name}
                      onSaved={(email) => {
                        setResults((prev) =>
                          prev.map((r) =>
                            r.id === result.id && r.type === result.type
                              ? { ...r, email, hasEmail: true }
                              : r,
                          ),
                        );
                        handleAdd({ id: result.id, type: result.type, name: result.name, email });
                      }}
                    />
                  </div>
                </div>
              );
            }

            return (
              <button
                key={`${result.type}-${result.id}`}
                type="button"
                disabled={disabled}
                title={
                  !result.hasEmail
                    ? 'Geen e-mailadres bekend — vul dit aan bij de contactgegevens'
                    : isAdded
                      ? 'Staat al in de lijst'
                      : undefined
                }
                onClick={() =>
                  handleAdd({ id: result.id, type: result.type, name: result.name, email: result.email })
                }
                className={`flex w-full items-center gap-2 px-2 py-1.5 text-left text-xs transition-colors ${
                  !result.hasEmail
                    ? 'cursor-not-allowed bg-amber-50/60'
                    : isAdded
                      ? 'cursor-not-allowed opacity-70'
                      : 'hover:bg-neutral-primary/5'
                }`}
              >
                {!result.hasEmail ? (
                  <Warning size={13} weight="fill" className="shrink-0 text-amber-700" />
                ) : result.type === 'company' ? (
                  <Buildings size={13} className="shrink-0 text-neutral-secondary-mid" />
                ) : (
                  <User size={13} className="shrink-0 text-neutral-secondary-mid" />
                )}
                <span className="min-w-0 flex-1">
                  <span className={`block truncate ${!result.hasEmail ? 'text-amber-900' : 'text-neutral-primary'}`}>
                    {result.name}
                  </span>
                  <span
                    className={`block truncate text-[10px] ${
                      !result.hasEmail ? 'text-amber-800' : 'text-neutral-secondary-mid'
                    }`}
                  >
                    {result.hasEmail ? `${result.subtitle} — ${result.email}` : `${result.subtitle} — geen e-mailadres`}
                  </span>
                </span>
                {isAdded && <span className="shrink-0 text-[10px] text-neutral-secondary-mid">toegevoegd</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
